<script setup lang="ts">
import {
  UiButtonVariant,
  UiAccordionVariant,
  UiHighlightDirection,
  UiOffersSize,
  type UiOffersItem,
} from '@afs/design-system'
import type UiAccordion from '@afs/design-system/components/molecules/UiAccordion/UiAccordion.vue.d.ts'

import { useI18n } from 'vue-i18n'
import { onMounted, ref } from 'vue'
import { useData } from 'vike-vue/useData'
import type { InsurancePageData } from './+data'
import { useFormatSeoContent } from '@/composables/useFormatSeoContent'
import { useGtmOnPageMounted } from '@/composables/useGtmTag'
import { openLegalNotice, scrollToLegalNotice } from '@/composables/useAccordion'

const { t } = useI18n()

const data = useData<InsurancePageData>()
useFormatSeoContent(data.insurance.seoTitle, data.insurance.hero.subtitle)
useGtmOnPageMounted('other')

const legalNoticeAccordion = ref<InstanceType<typeof UiAccordion> | null>(null)
onMounted(() => openLegalNotice(legalNoticeAccordion.value))

const breadcrumb = {
  ariaLabel: t('breadcrumb.ariaLabel'),
  items: [
    { text: t('page.accueil'), to: '/' },
    {
      text:
        data.insurance?.breadcrumbTitle && data.insurance.breadcrumbTitle.length > 40
          ? `${data.insurance.breadcrumbTitle.slice(0, 40)}...`
          : data.insurance?.breadcrumbTitle,
    },
  ],
}

const legalNotice = {
  id: 'insurance-legal-notice',
  title: t('homepage.mentionLegal.title'),
}
</script>

<template>
  <div
    v-if="data.insurance"
    class="Insurance"
  >
    <molecules-ui-breadcrumb
      v-bind="breadcrumb"
      class="Insurance__breadcrumb"
    />
    <organisms-ui-hero
      v-if="data.insurance.hero"
      v-bind="data.insurance.hero"
    />
    <div class="container my-8">
      <section
        v-if="data.insurance.argumentsBlock && data.insurance.promotionalBlock"
        class="Insurance__arguments grid"
      >
        <div
          v-if="data.insurance.argumentsBlock"
          class="Insurance__arguments__wrapper"
        >
          <h2 class="Insurance-arguments__title heading-2 color-blue-01">{{ data.insurance.argumentsBlock.title }}</h2>
          <ul class="Insurance-arguments__list g-3">
            <template
              v-for="(item, index) in data.insurance.argumentsBlock.arguments"
              :key="`Insurance-arguments-${index}`"
            >
              <li v-if="item">
                <molecules-ui-list-item :text="item" />
              </li>
            </template>
          </ul>
          <atoms-ui-button
            class="mt-4"
            :text="$t('insurance.mentionLegal.buttonLabel')"
            :variant="UiButtonVariant.TEXT"
            @click="scrollToLegalNotice(legalNoticeAccordion)"
          />
        </div>
        <div
          v-if="data.insurance.advisesBlock"
          class="Insurance__arguments__block bg-blue-02 radius-s pa-5"
        >
          <h2 class="heading-2 color-blue-01">{{ data.insurance.advisesBlock.title }}</h2>
          <molecules-ui-rich-text
            v-if="data.insurance.advisesBlock.content"
            class="mt-3"
            :content="data.insurance.advisesBlock.content"
          />
        </div>
      </section>

      <section
        v-if="data.insurance.promotionalBlock"
        class="mt-8 grid"
      >
        <molecules-ui-highlight
          class="Insurance__highlight"
          v-bind="data.insurance.promotionalBlock"
          :direction="UiHighlightDirection.HORIZONTAL"
        />
      </section>
    </div>

    <organisms-ui-offers
      v-if="data.insurance.guaranteeDetailsBlock"
      :title="data.insurance.guaranteeDetailsBlock.title"
      :sub-title="data.insurance.guaranteeDetailsBlock.subTitle"
      :offers="data.insurance.guaranteeDetailsBlock.card as UiOffersItem[]"
      :action-primary="data.insurance.guaranteeDetailsBlock.actionPrimary"
      :action-secondary="data.insurance.guaranteeDetailsBlock.actionSecondary"
      :size="UiOffersSize.LARGE"
    />

    <organisms-ui-help
      v-if="data.insurance.faqBlock && data.insurance.faqList"
      class="mb-8"
      :title="data.insurance.faqBlock.title"
      :contact="data.insurance.faqBlock.contact"
      :image="data.insurance.faqBlock.image"
    >
      <molecules-ui-accordion
        :items="data.insurance.faqList"
        :variant="UiAccordionVariant.SECONDARY"
        name="assurange-page-faq"
      >
        <template
          v-for="question in data.insurance.faqList"
          :key="question.id"
          #[question.id]
        >
          <h3
            v-if="question.subtitle"
            class="mb-2 heading-4"
          >
            {{ question.subtitle }}
          </h3>
          <molecules-ui-rich-text :content="question.content" />
        </template>
      </molecules-ui-accordion>
    </organisms-ui-help>

    <organisms-ui-related-articles
      v-if="data.insurance.articlesBlock.title && data.insurance.articlesBlock.articles"
      class="mb-8"
      :title="data.insurance.articlesBlock.title"
      :articles="data.insurance.articlesBlock.articles"
    />

    <div class="container">
      <section class="grid">
        <molecules-ui-accordion
          ref="legalNoticeAccordion"
          class="Insurance__legalNotice mt-4 mb-8"
          :items="[legalNotice]"
        >
          <template
            v-for="item in [legalNotice]"
            :key="item.id"
            #[item.id]
          >
            <molecules-ui-rich-text
              v-if="data.insurance.legalNoticeBlock.legalNotice"
              class="mt-3"
              :content="data.insurance.legalNoticeBlock.legalNotice"
            />
          </template>
        </molecules-ui-accordion>
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:map';
@use 'utilities/mq';
@use 'config/spaces';

.Insurance__breadcrumb {
  position: absolute;
  top: 0;
}

.Insurance__highlight,
.Insurance__legalNotice {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: 2 / span 10;
  }
}

.Insurance__legalNotice {
  --scroll-offset: 50px;

  @include mq.media('>=medium') {
    --scroll-offset: 200px;
  }

  &:deep(.Accordion__item) {
    scroll-margin: calc(var(--header-height) + var(--scroll-offset));
  }
}

.Insurance__arguments {
  align-items: center;

  @include mq.media('<medium') {
    row-gap: map.get(spaces.$sizes, '7');
  }
}

.Insurance__arguments__wrapper {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: 2 / span 5;
  }
}

.Insurance-arguments__title {
  margin-bottom: map.get(spaces.$sizes, '5');

  @include mq.media('>=medium') {
    margin-bottom: map.get(spaces.$sizes, '6');
  }
}

.Insurance-arguments__list {
  display: flex;
  flex-direction: column;
}

.Insurance__arguments__block {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: span 5;
  }
}
</style>
